import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useStaffProfile } from "../../../hooks/useStaffProfile";
import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import HomeNoticeBanner from "../../../components/notices/HomeNoticeBanner";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";

import { getRevokedModulesFromToken, isAdminFromToken } from "../../../utils/authToken";

import { FEFOPAGE } from "../../fefo/routes";

import ViewSubmittedEvaluationsModal from "../../../components/modal/evaluation/ViewSubmittedEvaluationsModal";
import { EvaluationFreelancerCandidate } from "../../../components/modal/evaluation/SelectFreelancersForEvaluationModal";

import { getStoresPaginatedApi } from "../../../views/store/storeApi";
import {
  getFreelancersBySlotApi,
  PaginatedSlotFreelancersResponse,
  SlotFreelancerItem,
} from "../../freelancer/freelancerApi";

import EcoSimbolo from "../../../img/eco/simbolo.png";

import { listSubmittedEvaluationsByRegistrationApi } from "../../supervisor/supervisorApi";

// `home.css` não é usado por esta tela desde o redesign, mas continua importado:
// é global no bundle e cerca de 20 telas ainda dependem das classes dele
// (`homeLayout`, `ecosystem__*`). Sai quando a última delas migrar.
import "./home.css";
import "./HomePage.css";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { PROCESSOS_ZE_PAGE } from "../../processos-ze/routes";
import { ESTOQUE_PAGE } from "../../estoque/routes";
import { DP_PAGE } from "../../dp/routes";
import { FINANCEIRO_PAGE } from "../../financeiro/routes";
import { ADMIN_SISTEMA_PAGE } from "../../admin-sistema/routes";
import { CAMPANHAS_PAGE } from "../../campanhas/routes";

type ModuleKey = "operacoes" | "processos" | "dp" | "financeiro" | "estoque" | "campanhas" | "administracao";

/** Card → módulo retirável do administrador restrito (ADR 0027). */
const ACCESS_MODULE_OF: Record<ModuleKey, string> = {
  operacoes: "OPERACOES",
  processos: "PROCESSOS",
  dp: "DP",
  financeiro: "FINANCEIRO",
  estoque: "ESTOQUE",
  campanhas: "CAMPANHAS",
  administracao: "ADMINISTRACAO",
};

type ModuleCard = {
  key: ModuleKey;
  title: string;
  description: string;
  onClick: () => void;
  icon: EcoIconName;
};

/** "Bom dia" / "Boa tarde" / "Boa noite" pelo relógio do aparelho. */
const saudacao = (agora: Date) => {
  const hora = agora.getHours();
  if (hora < 5 || hora >= 18) return "Boa noite";
  if (hora < 12) return "Bom dia";
  return "Boa tarde";
};

const dataPorExtenso = (agora: Date) =>
  new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" }).format(agora);

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const adminToken = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(adminToken), [adminToken]);
  const revokedModules = useMemo(() => getRevokedModulesFromToken(adminToken), [adminToken]);
  const { firstName } = useStaffProfile();

  const [isViewEvaluationsModalOpen, setIsViewEvaluationsModalOpen] = useState(false);

  // Calculado uma vez por montagem: a home não fica aberta tempo bastante para
  // a virada de período importar.
  const agora = useMemo(() => new Date(), []);

  const handleOpenViewEvaluations = useCallback(() => {
    if (!adminToken) {
      toast.error("Sessão inválida. Faça login novamente.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }
    setIsViewEvaluationsModalOpen(true);
  }, [adminToken, navigate]);

  const fetchStoresPage = useCallback(
    async (args: { token: string; page: number; limit: number }) => {
      return await getStoresPaginatedApi(args.token, { page: args.page, limit: args.limit });
    },
    [],
  );

  const fetchFreelancersForEvaluation = useCallback(
    async (args: {
      token: string;
      storeId: string;
      dayYmd: string;
      slotId: string;
    }): Promise<EvaluationFreelancerCandidate[]> => {
      const data: PaginatedSlotFreelancersResponse = await getFreelancersBySlotApi(
        args.token,
        args.slotId,
        { page: 1, limit: 200 },
      );

      const items: SlotFreelancerItem[] = data.items ?? [];
      return items.map((it) => ({
        registrationId: (it as any).registrationId,
        freelancerId: it.freelancerId,
        fullName: it.nomeCompleto,
        cpf: it.cpf,
        presenceStatus: it.status,
      }));
    },
    [],
  );

  const fetchSubmittedEvaluationsByRegistration = useCallback(
    async (args: { token: string; registrationId: string }) => {
      return await listSubmittedEvaluationsByRegistrationApi(args);
    },
    [],
  );

  const handleOperacoes = useCallback(() => {
    if (!isAdmin) {
      toast.error("Você não tem permissão para acessar Operações.");
      return;
    }
    navigate(OPERACOES_PAGE());
  }, [isAdmin, navigate]);

  const handleProcessosZe = useCallback(() => {
    if (!isAdmin) {
      toast.error("Você não tem permissão para acessar Operações.");
      return;
    }
    navigate(PROCESSOS_ZE_PAGE());
  }, [isAdmin, navigate]);

  const moduleCards: ModuleCard[] = useMemo(
    () => [
      {
        key: "operacoes",
        title: "Operações",
        description: "Lojas, escalas, freelancers e avaliações.",
        onClick: handleOperacoes,
        icon: "gear",
      },
      {
        key: "processos",
        title: "Processos Zé",
        description: "Monitorar e administrar processos.",
        onClick: handleProcessosZe,
        icon: "file",
      },
      {
        key: "dp",
        title: "Departamento Pessoal",
        description: "Colaboradores CLT, contratos, férias e folha.",
        onClick: () => navigate(DP_PAGE()),
        icon: "users",
      },
      {
        key: "financeiro",
        title: "Financeiro",
        description: "Contas a pagar e a receber, saldos e fluxo de caixa.",
        onClick: () => navigate(FINANCEIRO_PAGE()),
        icon: "dollar",
      },
      {
        key: "estoque",
        title: "Estoque",
        description: "Inventário, balanço de massa e vasilhame RGB.",
        onClick: () => navigate(ESTOQUE_PAGE()),
        icon: "box",
      },
      {
        key: "campanhas",
        title: "Campanhas",
        description: "Captação de freelancers no Facebook e no Instagram, e os candidatos.",
        onClick: () => navigate(CAMPANHAS_PAGE()),
        icon: "megaphone",
      },
      // Administração do Sistema é exclusiva de ADMIN e some para os demais, em
      // vez de aparecer e recusar com um toast — quem não pode entrar não
      // precisa saber que a porta existe.
      ...(isAdmin
        ? [
            {
              key: "administracao" as ModuleKey,
              title: "Administração do Sistema",
              description: "Usuários, freelancers e configurações.",
              onClick: () => navigate(ADMIN_SISTEMA_PAGE()),
              icon: "shield" as EcoIconName,
            },
          ]
        : []),
    ],
    [handleOperacoes, handleProcessosZe, navigate, isAdmin],
  );

  // Módulo retirado some, como a Administração para quem não é ADMIN.
  const visibleCards = useMemo(
    () => moduleCards.filter((card) => !revokedModules.includes(ACCESS_MODULE_OF[card.key])),
    [moduleCards, revokedModules],
  );
  const canOperacoes = !revokedModules.includes("OPERACOES");
  const canProcessos = !revokedModules.includes("PROCESSOS");

  return (
    <div className="ecoHome">
      <main className="ecoHome__main">
        <header className="ecoHome__header">
          <div className="ecoHome__greeting">
            <span className="ecoHome__date">{dataPorExtenso(agora)}</span>
            <h1 className="ecoHome__title">
              {saudacao(agora)}
              {firstName ? `, ${firstName}` : ""}
            </h1>
            <p className="ecoHome__subtitle">Escolha um módulo para começar.</p>
          </div>

          {/* Aviso não lido em destaque: o sino sozinho ficava discreto (22/10/2026). */}
          <HomeNoticeBanner />

          <div className="ecoHome__actions">
            {canOperacoes && (
              <button
                type="button"
                className="ecoHome__btn ecoHome__btn--ghost"
                onClick={handleOpenViewEvaluations}
              >
                <EcoIcon name="star" size={18} />
                Visualizar avaliações
              </button>
            )}
            {canProcessos && (
              <button
                type="button"
                className="ecoHome__btn ecoHome__btn--primary"
                onClick={() => navigate(FEFOPAGE())}
              >
                <EcoIcon name="thermometer" size={18} />
                FEFO
              </button>
            )}
          </div>
        </header>

        <section className="ecoHome__modules" aria-labelledby="ecoHome-modulos">
          <h2 id="ecoHome-modulos" className="ecoHome__sectionTitle">
            Módulos
          </h2>

          <div className="ecoHome__grid">
            {visibleCards.map((card) => (
              <button key={card.key} type="button" className={`ecoHome__card ecoHome__card--${card.key}`} onClick={card.onClick}>
                <span className="ecoHome__cardTop">
                  <span className="ecoHome__cardIcon">
                    <EcoIcon name={card.icon} size={24} strokeWidth={1.8} />
                  </span>
                  <EcoIcon name="arrowUpRight" className="ecoHome__cardArrow" />
                </span>
                <span className="ecoHome__cardText">
                  <span className="ecoHome__cardTitle">{card.title}</span>
                  <span className="ecoHome__cardDesc">{card.description}</span>
                </span>
              </button>
            ))}

            <aside className="ecoHome__info">
              <img className="ecoHome__infoSymbol" src={EcoSimbolo} alt="" aria-hidden="true" />
              <span className="ecoHome__infoTag">Eco System Serviços</span>
              <p className="ecoHome__infoText">
                Dúvidas sobre acesso ou permissões? Fale com a Administração.
              </p>
            </aside>
          </div>
        </section>

        <ViewSubmittedEvaluationsModal
          isOpen={isViewEvaluationsModalOpen}
          onClose={() => setIsViewEvaluationsModalOpen(false)}
          token={adminToken ?? ""}
          fetchStoresPage={fetchStoresPage}
          fetchFreelancersForEvaluation={fetchFreelancersForEvaluation}
          fetchSubmittedEvaluationsByRegistration={fetchSubmittedEvaluationsByRegistration}
        />
      </main>
    </div>
  );
};

export default HomePage;
